/**
 * LoggingConfigService - Frontend Runtime Log Configuration
 * 
 * A singleton service that manages logging configuration at runtime.
 * Fetches configuration from the backend and provides fast `isEnabled()` checks
 * for logger wrappers.
 * 
 * Features:
 * - Hot path optimization with caching
 * - Hierarchical inheritance resolution
 * - Real-time updates via WebSocket (when connected)
 * - Optimistic updates for UI responsiveness
 * 
 * @example
 * // Initialize on app bootstrap
 * await LoggingConfigService.initialize('my-realm');
 * 
 * // Check if logging is enabled (hot path - very fast)
 * if (LoggingConfigService.isEnabled('input.keyEvent')) {
 *   // ... do logging
 * }
 */

import { API_CONFIG, getDefaultHeaders } from '../config/api.config';
import { io, Socket } from 'socket.io-client';

// Helper to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('webelieve_access_token');
};

// Helper to get headers with auth
const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return getDefaultHeaders(token || undefined);
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface LoggingConfigEntry {
  enabled: boolean;
  source: 'global' | 'realm' | 'inherit' | 'default';
}

export interface LoggingConfig {
  [component: string]: LoggingConfigEntry;
}

export interface LoggingConfigResponse {
  realm: string | null;
  effectiveConfig: LoggingConfig;
  overrides: Array<{ component: string; enabled: boolean }>;
  lastUpdated: string;
}

export interface ComponentNode {
  id: string;
  label: string;
  description?: string;
  flavor?: boolean;
  children?: ComponentNode[];
}

export interface ComponentRegistryResponse {
  components: ComponentNode[];
}

export interface UpdateConfigResponse {
  success: boolean;
  component: string;
  enabled: boolean;
  realm: string | null;
  effectiveAt: string;
}

export interface BulkUpdateResponse {
  success: boolean;
  results: UpdateConfigResponse[];
}

type ConfigChangeListener = (component: string, enabled: boolean) => void;

// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================

class LoggingConfigServiceImpl {
  private static instance: LoggingConfigServiceImpl;

  private config: LoggingConfig = {};
  private realm: string | null = null;
  private listeners: Set<ConfigChangeListener> = new Set();
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  // Cache for fast lookups - the hot path
  private enabledCache: Map<string, boolean> = new Map();

  // Component registry (cached)
  private componentRegistry: ComponentRegistryResponse | null = null;

  // Socket.IO connection for real-time updates
  private socket: Socket | null = null;

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): LoggingConfigServiceImpl {
    if (!LoggingConfigServiceImpl.instance) {
      LoggingConfigServiceImpl.instance = new LoggingConfigServiceImpl();
    }
    return LoggingConfigServiceImpl.instance;
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize the service with the current realm
   * Called once during app bootstrap
   */
  async initialize(realm: string | null = null): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInitialize(realm);
    return this.initPromise;
  }

  private async _doInitialize(realm: string | null): Promise<void> {
    this.realm = realm;

    try {
      // Fetch initial config from backend
      await this.refreshConfig();

      // Connect Socket.IO for real-time config updates
      this.connectSocketIO();

      this.initialized = true;
      console.log('[LoggingConfigService] Initialized with realm:', realm, '| Components:', Object.keys(this.config).length);
    } catch (error) {
      console.warn('[LoggingConfigService] Initialization failed, defaulting to all enabled:', error);
      // On error, default to all enabled (fail-open for logging)
      this.config = {};
      this.initialized = true;
    }
  }

  /**
   * Check if the service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  // ============================================================================
  // HOT PATH: isEnabled()
  // ============================================================================

  /**
   * Check if logging is enabled for a component
   * 
   * This is the HOT PATH method called by all loggers on every log statement.
   * Must be extremely fast - uses caching for O(1) lookups.
   * 
   * @param component - Component identifier (e.g., 'input.keyEvent.start')
   * @returns true if logging is enabled, false otherwise
   */
  isEnabled(component: string): boolean {
    // If not initialized yet, default to DISABLED to prevent log spam during bootstrap
    if (!this.initialized || Object.keys(this.config).length === 0) {
      return false;
    }

    // Fast path: check cache
    const cached = this.enabledCache.get(component);
    if (cached !== undefined) {
      return cached;
    }

    // Slow path: resolve inheritance and cache result
    const enabled = this.resolveEnabled(component);
    this.enabledCache.set(component, enabled);
    return enabled;
  }

  /**
   * Resolve enabled state with inheritance
   * Walks up the component hierarchy until a config is found
   */
  private resolveEnabled(component: string): boolean {
    // Check exact match first
    if (this.config[component]) {
      return this.config[component].enabled;
    }

    // Walk up the hierarchy (input.keyEvent.start -> input.keyEvent -> input)
    const parts = component.split('.');
    for (let i = parts.length - 1; i > 0; i--) {
      const parent = parts.slice(0, i).join('.');
      if (this.config[parent]) {
        return this.config[parent].enabled;
      }
    }

    // Default: DISABLED - if no config exists for component, don't log
    // This ensures disabled components stay disabled until explicitly enabled
    return false;
  }

  // ============================================================================
  // CONFIGURATION MANAGEMENT
  // ============================================================================

  /**
   * Refresh config from backend
   */
  async refreshConfig(): Promise<void> {
    try {
      const url = `${API_CONFIG.BASE_URL}/api/v1/logging/config${this.realm ? `?realm=${encodeURIComponent(this.realm)}` : ''}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch logging config: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        this.config = data.data.effectiveConfig;
      } else {
        this.config = data.effectiveConfig || {};
      }

      // Clear cache on refresh
      this.enabledCache.clear();

      console.log('[LoggingConfigService] Config refreshed:', Object.keys(this.config).length, 'components');
    } catch (error) {
      console.error('[LoggingConfigService] Failed to refresh config:', error);
      // Keep existing config on error
    }
  }

  /**
   * Update a component's enabled state
   * Realm is determined by the backend from the JWT token
   */
  async updateConfig(component: string, enabled: boolean, reason?: string): Promise<UpdateConfigResponse> {
    const url = `${API_CONFIG.BASE_URL}/api/v1/logging/config/${encodeURIComponent(component)}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        enabled,
        reason,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update logging config: ${response.status}`);
    }

    const data = await response.json();

    // Optimistically update local state
    this.applyConfigChange(component, enabled);

    return data.data || data;
  }

  /**
   * Bulk update multiple components
   * Realm is determined by the backend from the JWT token
   */
  async bulkUpdate(
    updates: Array<{ component: string; enabled: boolean }>,
    reason?: string
  ): Promise<BulkUpdateResponse> {
    const url = `${API_CONFIG.BASE_URL}/api/v1/logging/config/bulk`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates,
        reason,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to bulk update logging config: ${response.status}`);
    }

    const data = await response.json();

    // Optimistically update local state
    for (const update of updates) {
      this.applyConfigChange(update.component, update.enabled);
    }

    return data.data || data;
  }

  /**
   * Remove a realm-specific override
   */
  async removeOverride(component: string): Promise<void> {
    if (!this.realm) {
      throw new Error('Cannot remove override without a realm');
    }

    const url = `${API_CONFIG.BASE_URL}/api/v1/logging/config/${encodeURIComponent(component)}?realm=${encodeURIComponent(this.realm)}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to remove override: ${response.status}`);
    }

    // Refresh to get inherited value
    await this.refreshConfig();
  }

  /**
   * Apply a config change locally (optimistic update)
   */
  private applyConfigChange(component: string, enabled: boolean): void {
    // Update config
    this.config[component] = {
      enabled,
      source: this.realm ? 'realm' : 'global',
    };

    // Clear cache for this component and all children
    for (const key of this.enabledCache.keys()) {
      if (key === component || key.startsWith(component + '.')) {
        this.enabledCache.delete(key);
      }
    }

    // Notify listeners
    for (const listener of this.listeners) {
      try {
        listener(component, enabled);
      } catch (error) {
        console.error('[LoggingConfigService] Listener error:', error);
      }
    }
  }

  // ============================================================================
  // COMPONENT REGISTRY
  // ============================================================================

  /**
   * Get the component registry
   */
  async getComponentRegistry(): Promise<ComponentRegistryResponse> {
    if (this.componentRegistry) {
      return this.componentRegistry;
    }

    const url = `${API_CONFIG.BASE_URL}/api/v1/logging/config/components`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch component registry: ${response.status}`);
    }

    const data = await response.json();
    this.componentRegistry = data.data || data;

    return this.componentRegistry!;
  }

  // ============================================================================
  // REALM MANAGEMENT
  // ============================================================================

  /**
   * Get the current configuration
   */
  getConfig(): LoggingConfig {
    return { ...this.config };
  }

  /**
   * Get the current realm
   */
  getRealm(): string | null {
    return this.realm;
  }

  /**
   * Change the current realm (e.g., admin switching contexts)
   */
  async setRealm(realm: string | null): Promise<void> {
    this.realm = realm;
    this.enabledCache.clear();
    await this.refreshConfig();

    // Re-subscribe Socket.IO with new realm if connected
    if (this.socket?.connected) {
      this.socket.emit('subscribe', { realm });
    }
  }

  // ============================================================================
  // EVENT LISTENERS
  // ============================================================================

  /**
   * Subscribe to config changes
   * @returns Unsubscribe function
   */
  onConfigChange(listener: ConfigChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ============================================================================
  // SOCKET.IO (REAL-TIME SYNC)
  // ============================================================================

  /**
   * Connect to Socket.IO for real-time config updates
   */
  private connectSocketIO(): void {
    try {
      const token = getAuthToken();
      if (!token) {
        console.warn('[LoggingConfigService] No auth token, skipping Socket.IO connection');
        return;
      }

      // Connect to logging-config namespace using Socket.IO
      this.socket = io('/logging-config', {
        path: '/socket.io', // Match backend Socket.IO path
        auth: { token },
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
      });

      this.socket.on('connect', () => {
        console.log('[LoggingConfigService] Socket.IO connected');
        // Subscribe to realm-specific updates
        this.socket?.emit('subscribe', { realm: this.realm });
      });

      this.socket.on('subscribed', (data: { realm: string; timestamp: string }) => {
        console.log('[LoggingConfigService] Subscribed to realm:', data.realm);
      });

      this.socket.on('config-change', (data: { 
        component: string; 
        enabled: boolean; 
        realm: string | null;
        updatedBy?: string;
        timestamp: string;
      }) => {
        console.log('[LoggingConfigService] Real-time config change:', data);
        this.applyConfigChange(data.component, data.enabled);
      });

      this.socket.on('bulk-config-change', (data: {
        changes: Array<{ component: string; enabled: boolean }>;
        realm: string | null;
        timestamp: string;
      }) => {
        console.log('[LoggingConfigService] Real-time bulk config change:', data.changes.length, 'components');
        for (const change of data.changes) {
          this.applyConfigChange(change.component, change.enabled);
        }
      });

      this.socket.on('disconnect', (reason) => {
        console.log('[LoggingConfigService] Socket.IO disconnected:', reason);
      });

      this.socket.on('connect_error', (error) => {
        console.warn('[LoggingConfigService] Socket.IO connection error:', error.message);
      });

    } catch (error) {
      console.warn('[LoggingConfigService] Failed to connect Socket.IO:', error);
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Clean up resources
   */
  dispose(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.listeners.clear();
    this.enabledCache.clear();
    this.initialized = false;
    this.initPromise = null;
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Singleton instance of LoggingConfigService
 * 
 * @example
 * import { LoggingConfigService } from './services/LoggingConfigService';
 * 
 * // Initialize on app bootstrap
 * await LoggingConfigService.initialize('my-realm');
 * 
 * // Use in logger wrappers
 * if (LoggingConfigService.isEnabled('input.keyEvent')) {
 *   // ... do logging
 * }
 */
export const LoggingConfigService = LoggingConfigServiceImpl.getInstance();

// Export types
export type { ConfigChangeListener };
